import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useTranslations } from '@/hooks/useTranslations';
import { router } from '@inertiajs/react';
import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface ItemTranslations {
    en: Record<string, string>;
    lt: Record<string, string>;
}

interface ManageSettingItemsDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    settingKey: string;
    setting: {
        value: unknown;
        translation_group?: string | null;
        item_translations?: ItemTranslations | null;
    };
    onSuccess?: () => void;
}

interface RowState {
    value: string;
    en: string;
    lt: string;
}

export function ManageSettingItemsDialog({ open, onOpenChange, settingKey, setting, onSuccess }: ManageSettingItemsDialogProps) {
    const { t } = useTranslations();
    const [saving, setSaving] = useState(false);
    const [deletingValue, setDeletingValue] = useState<string | null>(null);

    // Build the editable rows from the setting's current values, seeding the
    // EN/LT inputs with whatever is already in the lang files.
    const rows = useMemo<RowState[]>(() => {
        const settingValue = setting.value;
        const translations = setting.item_translations;

        const values: string[] = Array.isArray(settingValue)
            ? settingValue.map((item: unknown) => (typeof item === 'string' ? item : String((item as { value?: unknown })?.value ?? item)))
            : [];

        return values.map((value) => ({
            value,
            en: translations?.en?.[value] ?? '',
            lt: translations?.lt?.[value] ?? '',
        }));
    }, [setting.value, setting.item_translations]);

    const [drafts, setDrafts] = useState<Record<string, { en: string; lt: string }>>({});

    const busy = saving || deletingValue !== null;

    const draftFor = (row: RowState) => drafts[row.value] ?? { en: row.en, lt: row.lt };

    const setDraft = (value: string, patch: Partial<{ en: string; lt: string }>) => {
        setDrafts((prev) => {
            const current = prev[value] ?? { en: rows.find((r) => r.value === value)?.en ?? '', lt: rows.find((r) => r.value === value)?.lt ?? '' };
            return { ...prev, [value]: { ...current, ...patch } };
        });
    };

    // Rows whose EN/LT draft differs from what's currently saved.
    const dirtyRows = rows.filter((row) => {
        const draft = draftFor(row);
        return draft.en !== row.en || draft.lt !== row.lt;
    });

    const handleSaveAll = () => {
        if (dirtyRows.length === 0) {
            return;
        }

        setSaving(true);

        router.post(
            route('settings.system.update-items', settingKey),
            {
                items: dirtyRows.map((row) => {
                    const draft = draftFor(row);
                    return { value: row.value, en: draft.en || row.value, lt: draft.lt || row.value };
                }),
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    toast.success(t('settings.manage_items.saved', 'Translations updated'));
                    setDrafts({});
                    onSuccess?.();
                },
                onError: () => {
                    toast.error(t('settings.manage_items.save_failed', 'Failed to update translations'));
                },
                onFinish: () => setSaving(false),
            },
        );
    };

    const handleDelete = (row: RowState) => {
        setDeletingValue(row.value);

        router.delete(route('settings.system.remove-item', settingKey), {
            data: { value: row.value },
            preserveScroll: true,
            onSuccess: () => {
                toast.success(t('settings.manage_items.removed', 'Item removed'));
                onSuccess?.();
            },
            onError: () => {
                toast.error(t('settings.manage_items.remove_failed', 'Failed to remove item'));
            },
            onFinish: () => setDeletingValue(null),
        });
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('settings.manage_items.title', 'Manage items & translations')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'settings.manage_items.description',
                            'Edit the English and Lithuanian labels for each item, or remove items you no longer need.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="space-y-4">
                    {rows.length === 0 && (
                        <p className="text-muted-foreground text-sm">{t('settings.manage_items.empty', 'There are no items to manage yet.')}</p>
                    )}

                    {rows.length > 0 && (
                        <div className="text-muted-foreground hidden grid-cols-[1fr_1fr_1fr_auto] gap-3 px-1 text-xs font-medium sm:grid">
                            <span>{t('settings.manage_items.value', 'Value')}</span>
                            <span>{t('settings.manage_items.english', 'English')}</span>
                            <span>{t('settings.manage_items.lithuanian', 'Lithuanian')}</span>
                            <span />
                        </div>
                    )}

                    {rows.map((row) => {
                        const draft = draftFor(row);
                        const isDeleting = deletingValue === row.value;

                        return (
                            <div key={row.value} className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
                                <div className="space-y-1">
                                    <Label className="text-muted-foreground text-xs sm:hidden">{t('settings.manage_items.value', 'Value')}</Label>
                                    <div className="bg-muted/50 truncate rounded-md border px-3 py-2 text-sm" title={row.value}>
                                        {row.value}
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-muted-foreground text-xs sm:hidden">{t('settings.manage_items.english', 'English')}</Label>
                                    <Input
                                        value={draft.en}
                                        onChange={(e) => setDraft(row.value, { en: e.target.value })}
                                        placeholder={t('settings.manage_items.english', 'English')}
                                        disabled={busy}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-muted-foreground text-xs sm:hidden">
                                        {t('settings.manage_items.lithuanian', 'Lithuanian')}
                                    </Label>
                                    <Input
                                        value={draft.lt}
                                        onChange={(e) => setDraft(row.value, { lt: e.target.value })}
                                        placeholder={t('settings.manage_items.lithuanian', 'Lithuanian')}
                                        disabled={busy}
                                    />
                                </div>
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="outline"
                                    onClick={() => handleDelete(row)}
                                    disabled={busy}
                                    title={t('settings.manage_items.remove', 'Remove item')}
                                >
                                    <Trash2 className={isDeleting ? 'h-4 w-4 animate-pulse' : 'h-4 w-4'} />
                                </Button>
                            </div>
                        );
                    })}
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        {t('settings.manage_items.close', 'Close')}
                    </Button>
                    <Button type="button" onClick={handleSaveAll} disabled={busy || dirtyRows.length === 0}>
                        {saving
                            ? t('settings.manage_items.saving', 'Saving...')
                            : dirtyRows.length > 0
                              ? t('settings.manage_items.save_count', 'Save changes (:count)').replace(':count', String(dirtyRows.length))
                              : t('settings.manage_items.save_changes', 'Save changes')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
